import { createApiClient, type ApiClient } from './client';
import { refreshSession } from './auth';
import { useAuthStore } from '../store/auth';
import { usePaywallStore } from '../store/paywall';
import { redirectToLogin } from '../lib/redirect';

let _client: ApiClient | null = null;

/** Lazy-singleton ApiClient для всего приложения. Origin — пустая строка
 *  в dev (vite-proxy), либо `VITE_API_ORIGIN` в prod. */
export function getApiClient(): ApiClient {
  if (_client) return _client;
  const origin = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';
  _client = createPanelApiClient(origin);
  return _client;
}

/** Тестовый хук: подменить singleton до первого использования или сбросить. */
export function _setApiClientForTest(c: ApiClient | null): void {
  _client = c;
}

/**
 * Создаёт ApiClient, привязанный к глобальному authStore. Сам по себе
 * client не знает про store — здесь мы соединяем зависимости:
 *  - access берётся из store.session.access
 *  - 401 → /auth/refresh, ответ кладём в store.setSession()
 *  - refresh-fail → store.clear()
 *
 * Для запроса /auth/refresh используется отдельный "raw" client без
 * автоматического retry — иначе при 401 на /auth/refresh случилась бы
 * бесконечная рекурсия.
 */
export function createPanelApiClient(origin: string): ApiClient {
  const rawClient: ApiClient = createApiClient({
    origin,
    getAccess: () => null,
    refresh: async () => null,
    onAuthFail: () => {},
  });

  return createApiClient({
    origin,
    getAccess: () => useAuthStore.getState().session?.access ?? null,
    refresh: async () => {
      const session = useAuthStore.getState().session;
      if (!session) return null;
      try {
        const next = await refreshSession(rawClient, session.refresh);
        useAuthStore.getState().setSession(next);
        return next.access;
      } catch {
        return null;
      }
    },
    // ANX-117: чистим сессию И уводим на логин. Раньше только clear() —
    // роутер не редиректил реактивно, оператор застревал с «failed to fetch»
    // под композером вместо понятного экрана входа.
    onAuthFail: () => {
      useAuthStore.getState().clear();
      redirectToLogin();
    },
    // ANX-105: 402-лимит → глобальный апселл-диалог (PaywallDialog в AppShell).
    onPaywall: (code) => usePaywallStore.getState().open(code),
  });
}
